import {
  compareOrdered,
  MAX_HOUSEHOLD_RECIPES,
  normalizeTag,
  type Collection,
  type CollectionId,
  type Recipe,
  type RecipeQuery,
  type RecipeStore,
} from '@platewell/core';
import type { RecipeSearchIndex } from './search.js';
import { hasSearchFilters, parseSearchTerm, type ParsedSearch } from './search-syntax.js';

export interface CollectionSummary extends Collection {
  recipeCount: number;
  /** Blob ids of the first few photos, for a collection cover mosaic. */
  coverBlobIds: string[];
}

export interface TagFacet {
  tag: string;
  count: number;
}

export interface RecipeSummary {
  id: string;
  title: string;
  description: string;
  primaryBlobId: string | null;
  blurhash: string;
  tags: readonly string[];
  totalMinutes: number | null;
  updatedAt: number;
  deletedAt: number | null;
}

/** The list-view shape. Lists never need ingredients, and skipping them keeps rendering cheap. */
export function toSummary(recipe: Recipe): RecipeSummary {
  const primary = recipe.photos[0] ?? null;
  const total =
    recipe.prepMinutes == null && recipe.cookMinutes == null
      ? null
      : (recipe.prepMinutes ?? 0) + (recipe.cookMinutes ?? 0);
  return {
    id: recipe.id,
    title: recipe.title,
    description: recipe.description,
    primaryBlobId: primary?.blobId ?? null,
    blurhash: primary?.blurhash ?? '',
    tags: recipe.tags,
    totalMinutes: total,
    updatedAt: recipe.updatedAt,
    deletedAt: recipe.deletedAt,
  };
}

export function collectionSummaries(store: RecipeStore): CollectionSummary[] {
  const counts = new Map<CollectionId, number>();
  const covers = new Map<CollectionId, string[]>();

  for (const recipe of store.listRecipes({ limit: Number.MAX_SAFE_INTEGER })) {
    for (const collectionId of recipe.collectionIds) {
      counts.set(collectionId, (counts.get(collectionId) ?? 0) + 1);
      const cover = covers.get(collectionId) ?? [];
      if (cover.length < 4 && recipe.photos[0] != null) cover.push(recipe.photos[0].blobId);
      covers.set(collectionId, cover);
    }
  }

  return store
    .listCollections()
    .map((collection) => ({
      ...collection,
      recipeCount: counts.get(collection.id) ?? 0,
      coverBlobIds: covers.get(collection.id) ?? [],
    }))
    .sort(compareOrdered);
}

export function tagFacets(store: RecipeStore, limit = 50): TagFacet[] {
  const facets = store.listTags();
  const mode = store.getTagDisplayCase();
  const visible = mode === 'lower' || mode === 'title' ? mergeCaseBlindTagFacets(facets) : facets;
  return visible.slice(0, limit);
}

/** Fold tags that differ only by case, keeping the most common stored spelling. */
function mergeCaseBlindTagFacets(facets: readonly TagFacet[]): TagFacet[] {
  const merged = new Map<string, TagFacet>();
  for (const facet of facets) {
    const key = facet.tag.toLowerCase();
    const existing = merged.get(key);
    if (existing == null) {
      merged.set(key, { tag: facet.tag, count: facet.count });
    } else {
      existing.count += facet.count;
    }
  }
  return [...merged.values()].sort((a, b) => (b.count - a.count) || a.tag.localeCompare(b.tag));
}

export interface BrowseQuery extends RecipeQuery {
  /**
   * Full-text term. Applied through MiniSearch rather than the store's substring filter.
   * May carry `tag:` and `collection:` operators, see {@link parseSearchTerm}.
   */
  readonly term?: string;
}

/**
 * Resolves a browse query into an ordered list.
 *
 * Searching and filtering are separate steps on purpose: MiniSearch decides *relevance*,
 * the store decides *membership and order*. Intersecting them means a search inside a
 * collection stays inside that collection, and clearing the search returns to the user's
 * hand-arranged order rather than to a relevance ranking.
 *
 * Search operators only narrow membership, so a term that is nothing but operators keeps
 * the list's own order rather than switching to relevance.
 */
export function browse(
  store: RecipeStore,
  index: RecipeSearchIndex | null,
  query: BrowseQuery = {},
): Recipe[] {
  const { term, ...rest } = query;
  const parsed = parseSearchTerm(term ?? '');
  const limit = rest.limit ?? 200;
  const filtered = hasSearchFilters(parsed);

  if (parsed.text === '' || index == null) {
    if (!filtered) return store.listRecipes(rest);
    const matches = searchFilter(store, parsed);
    return store
      .listRecipes({ ...rest, limit: Number.MAX_SAFE_INTEGER })
      .filter(matches)
      .slice(0, limit);
  }

  const matches = filtered ? searchFilter(store, parsed) : () => true;
  // Operators are applied after ranking, so rank everything rather than cutting off at the
  // limit and then losing rows to the filter.
  const ranked = index.search(parsed.text, filtered ? Number.MAX_SAFE_INTEGER : limit);
  const rank = new Map(ranked.map((hit, position) => [hit.id, position]));
  return store
    .listRecipes({ ...rest, limit: Number.MAX_SAFE_INTEGER })
    .filter((recipe) => rank.has(recipe.id) && matches(recipe))
    .sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0))
    .slice(0, limit);
}

/**
 * Every `tag:` and `collection:` must match, like every word of a search, and any excluded
 * one rules the recipe out. Collections are named rather than identified in a search, so a
 * name that matches nothing leaves an include with no results and an exclude with no effect.
 */
function searchFilter(store: RecipeStore, parsed: ParsedSearch): (recipe: Recipe) => boolean {
  const collections = store.listCollections();
  const idsNamed = (name: string): Set<CollectionId> =>
    new Set(
      collections
        .filter((collection) => normalizeTag(collection.name).toLowerCase() === name)
        .map((collection) => collection.id),
    );
  const includeCollections = parsed.includeCollections.map(idsNamed);
  const excludeCollections = new Set(parsed.excludeCollections.flatMap((name) => [...idsNamed(name)]));

  return (recipe) => {
    const tags = new Set(recipe.tags.map((tag) => tag.toLowerCase()));
    if (!parsed.includeTags.every((tag) => tags.has(tag))) return false;
    if (parsed.excludeTags.some((tag) => tags.has(tag))) return false;
    const inAny = (ids: Set<CollectionId>) => recipe.collectionIds.some((id) => ids.has(id));
    if (!includeCollections.every(inAny)) return false;
    return !recipe.collectionIds.some((id) => excludeCollections.has(id));
  };
}

/** Recipes in the trash, most recently deleted first. */
export function trashed(store: RecipeStore): Recipe[] {
  return store
    .listRecipes({ onlyDeleted: true, limit: Number.MAX_SAFE_INTEGER })
    .sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0));
}

export interface RecipeQuotaView {
  /** null means unlimited. */
  limit: number | null;
  used: number;
  /** null means unlimited. */
  remaining: number | null;
  /** At the plan's limit, which subscribing lifts. */
  atLimit: boolean;
  /** At `MAX_HOUSEHOLD_RECIPES`, which no plan lifts. Never offer the paywall for this. */
  atCeiling: boolean;
  canAdd: boolean;
  /** True from two slots out, which is where the nudge banner starts. */
  approaching: boolean;
}

/** How many slots the nudge banner appears at, counting down from the limit. */
export const QUOTA_NUDGE_THRESHOLD = 2;

/**
 * The plan cap as the UI needs it, in one shape.
 *
 * Pure and store-only, like `browse` and `tagFacets`, so the banner's behaviour at the
 * boundary is testable without rendering anything.
 */
export function recipeQuota(store: RecipeStore): RecipeQuotaView {
  const limit = store.getRecipeLimit();
  const remaining = store.remainingRecipeSlots();
  // Not `listRecipes().length`: that materialises every ingredient and photo in the book
  // on every render. `liveRecipeCount` reads one field per recipe.
  const used = store.liveRecipeCount();
  const atCeiling = used >= MAX_HOUSEHOLD_RECIPES;

  if (limit == null || remaining == null) {
    return {
      limit: null,
      used,
      remaining: null,
      atLimit: false,
      atCeiling,
      canAdd: !atCeiling,
      approaching: false,
    };
  }
  return {
    limit,
    used,
    remaining,
    atLimit: remaining === 0,
    atCeiling,
    canAdd: remaining > 0 && !atCeiling,
    approaching: remaining > 0 && remaining <= QUOTA_NUDGE_THRESHOLD,
  };
}
